/**
 * Plain-language labels for the terminal's access error codes (AC guide §5.11.5 "ErrorCode").
 * Deviation: this catalogue belongs in @nacs/shared next to UNLOCK_METHODS; move it there when the
 * package is published to GitHub Packages (rules/00 §1.4). Keys are decimal; the guide prints hex,
 * so each block below carries the hex it came from — a code read off a packet capture is hex, and
 * mapping it by hand is how a wrong row gets labelled.
 *
 * **A code with no entry here falls back to "Reason 83", which is exactly the protocol jargon
 * rules/01 §4 forbids on screen** — so the table is filled from the guide rather than from the codes
 * that happened to turn up. What is NOT filled in matters as much: a label nobody can source is
 * worse than a number, because a number invites a question and a confident wrong sentence does not.
 * The codes deliberately left to the numeric fallback are listed at the bottom of this comment.
 *
 * Sources, and there are only two:
 *
 *  - **the AC guide §5.11.5** for everything except 83;
 *  - **measurement on the desk ASI6214S, 2026-09-17** for 83 (`01-dahua-protocol-notes.md` §8,
 *    ADR-0021), which the guide does not list at all: its table runs 0x52 → 0x60 with 0x53 missing.
 *
 * Left to the numeric fallback on purpose:
 *
 *  - **0x64 (100)** — "unauthorized, needs the back-end platform", a customisation for one airport.
 *    What it means at a door depends on a system we do not have, so any sentence would be a guess.
 *  - **0x70, 0x71 (112, 113)** — instructions to the PLATFORM ("return health code information"),
 *    not outcomes of somebody presenting a credential. Rendering them as a refusal reason would be
 *    a wrong sentence, not a missing one.
 *  - **0xA1 (161)** — "the user already exists", a management error, not a door outcome.
 *  - **0xA9 (169)** — "Illegal Card Exceeding Time". The guide's whole entry is those four words and
 *    nothing here can turn them into a sentence a receptionist could act on.
 */
const ACCESS_REASONS: Record<number, string> = {
  0: "No error",

  // 0x10–0x1D — the credential, the door and the device's own mode.
  16: "Not enrolled",
  17: "Card cancelled",
  18: "No permission for this door",
  19: "Unlock mode not allowed",
  20: "Outside validity period",
  21: "Anti-passback",
  22: "Duress alarm off",
  23: "Door set to normally closed",
  24: "Interlock active",
  25: "Patrol card",
  26: "Intrusion alarm active",
  27: "Device locked",
  28: "Do-not-disturb mode",
  29: "Device in non-user mode",

  // 0x20–0x25 — when, and whether the card is paid up.
  32: "Outside allowed period",
  33: "Holiday period",
  34: "Card in arrears",
  35: "Card expired",
  36: "Card pre-arrears",
  37: "Blocklisted card",

  // 0x30 — first-card rule.
  48: "First-card rule",

  /*
   * 0x40–0x47 — **one credential of a pair was right and the other was not**. The guide words these
   * as "Correct card, password error"; said that way round on screen they tell a desk which half to
   * look at, which is the whole value of the code. A "timeout" is the second credential never being
   * presented, not a network timeout.
   */
  64: "Wrong password",
  65: "Password timeout",
  66: "Card recognised, fingerprint did not match",
  67: "Card recognised, no fingerprint given in time",
  68: "Fingerprint recognised, wrong password",
  69: "Fingerprint recognised, no password given in time",
  70: "User ID recognised, wrong password",
  71: "User ID recognised, no password given in time",

  /*
   * 0x50–0x52 — multi-person unlocking. 81 and 82 are not refusals at all: the terminal is saying
   * "so far so good, the next person may verify", and a row carrying one is a half-finished unlock
   * rather than somebody turned away.
   */
  80: "Multi-person unlock out of order",
  81: "Waiting for the next person to verify",
  82: "Waiting for the second credential",

  /*
   * 0x53 (83) — **not in the guide.** Measured on the desk terminal: it is what the reader reports
   * for a QR code it holds no credential for, which is EVERY QR code, because the firmware cannot
   * hold one (protocol notes §8, ADR-0021). It is the most common denial in the system for that
   * reason.
   *
   * The sentence is about the TERMINAL, deliberately, because that is all this number ever says. A
   * pass WE refused — expired, revoked, out of uses, outside its cooldown — keeps the terminal's 83
   * on the row (the worker's `applyOutcome` only overwrites the code when it opened the door), so a
   * label claiming to know why the pass failed would be wrong on exactly those rows. Our own reason
   * is shown live on the visitors scan feed, where it belongs.
   */
  83: "QR code not recognised by the terminal",

  // 0x60–0x63 — the console's own verdict, face-plus-card pairs, and a repeat.
  96: "Verified, but the console refused it",
  97: "Card recognised, face did not match",
  98: "Card recognised, no face shown in time",
  99: "Repeated entry",

  // 0x65–0x6B — the health screening block. Rare here, and real: the firmware still reports them.
  101: "Body temperature too high",
  102: "No face mask",
  103: "Could not read the health code",
  104: "Yellow health code",
  105: "Red health code",
  106: "Health code not valid",
  107: "Green health code",

  // 0xA0–0xA8 — the later additions.
  160: "Wrong custom password",
  162: "Single-person unlock out of order",
  163: "Not in the multi-person unlock group",
  164: "User account frozen",
  165: "Guest entry limit reached",
  166: "Lift control timed out",
  167: "Too many wrong password attempts",
  168: "No safety helmet",
};

export function accessReasonLabel(errorCode: number | null | undefined): string | null {
  if (errorCode === null || errorCode === undefined) return null;
  return ACCESS_REASONS[errorCode] ?? `Reason ${errorCode}`;
}
